import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuBoxes, LuCalendarClock, LuInfo, LuLayers, LuPackage, LuPackageCheck, LuPackageX, LuTag, LuTrendingDown, LuTruck, LuWrench, LuX } from 'react-icons/lu';
import DataCardList from '../components/data/DataCardList';
import DataTable from '../components/data/DataTable';
import DataToolbar from '../components/data/DataToolbar';
import FilterSheet from '../components/data/FilterSheet';
import StatTiles from '../components/data/StatTiles';
import TablePagination from '../components/data/TablePagination';
import ToggleChip from '../components/data/ToggleChip';
import SegmentedControl from '../components/ui/SegmentedControl';
import { SelectInput } from '../components/form/FormControls';
import useElementWidth from '../hooks/useElementWidth';
import useMediaQuery from '../hooks/useMediaQuery';
import usePagedList from '../hooks/usePagedList';
import useTableColumns from '../hooks/useTableColumns';
import useViewportFit from '../hooks/useViewportFit';
import CargoTracking from './StockManagement/CargoTracking/CargoTracking';
import StockReportModal from './StockManagement/StockReportModal';
import useStockManagement from './StockManagement/useStockManagement';
import useStockText from './StockManagement/stockText';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  STOCK_COLUMNS,
  getFilterState,
} from './StockManagement/stockColumns';
import StockHeader from './StockManagement/parts/StockHeader';
import StockCell, { StockCardRow, getCellTitle } from './StockManagement/parts/StockCell';
import StockDetailPane from './StockManagement/parts/StockDetailPane';
import StockFormModal from './StockManagement/parts/StockFormModal';
import StockAssignModal from './StockManagement/parts/StockAssignModal';
import StockHistoryModal from './StockManagement/parts/StockHistoryModal';
import StockTypesModal from './StockManagement/parts/StockTypesModal';
import AssignedStocksModal from './StockManagement/parts/AssignedStocksModal';

const COLUMNS_STORAGE_KEY = 'stockManagement.tableColumns.v1';
// From this width the stock panel sits beside the table instead of over it
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

// Segment label with an icon and a count; `shortText` replaces the text on phones
const withCount = (icon, text, count, shortText) => {
  const Icon = icon;
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap">
      <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      {shortText ? (
        <>
          <span className="sm:tw-hidden">{shortText}</span>
          <span className="tw-hidden sm:tw-inline">{text}</span>
        </>
      ) : text}
      {count !== undefined && (
        <span className="tw-rounded-full tw-bg-slate-900/[0.07] tw-px-1.5 tw-text-[10px] tw-font-bold tw-tabular-nums dark:tw-bg-white/10">{count}</span>
      )}
    </span>
  );
};

/**
 * Stok Yönetimi (Tailwind), built like Çalışan / Müşteri Yönetimi: head-count
 * tiles that filter, a table card that reaches at most the bottom of the
 * screen with header filters and a column menu, and the selected stock's
 * panel beside the table (a drawer on narrower screens). Every action of the
 * old page is still here; the data logic lives in useStockManagement.
 */
const StockManagementPage = () => {
  const stock = useStockManagement();
  const {
    stocks, stocksLoaded, filteredStocks, stockTypes, customers, projects, activeEmployees, isLowStock,
    categoryStocks, sarfCount, demirbasCount, activeStocks, inactiveStocks, lowStockItems, fetchStocks,
    filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    showExpiringOnly, setShowExpiringOnly, showLowStockOnly, setShowLowStockOnly,
    cardFilter, setCardFilter, categoryFilter, setCategoryFilter, selectedTypeFilter, setSelectedTypeFilter,
    activeModule, setActiveModule, selectedStock, setSelectedStock, handleStockSelect,
  } = stock;
  const { language, locale, label, ownershipLabel, statusLabel } = useStockText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: STOCK_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  // Phones get the compact segment size so the tabs fit without scrolling
  const segmentSize = useMediaQuery('(min-width: 640px)') ? 'md' : 'sm';
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  // Süzgeçler değişince ilk sayfaya dönülür (eski sayfadaki gibi)
  const resetKey = JSON.stringify([filters, searchTerm, showExpiringOnly, showLowStockOnly, cardFilter, selectedTypeFilter, categoryFilter]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredStocks, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  const onStocks = activeModule === 'stocks';

  // Escape closes the selected stock's panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedStock || !onStocks) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedStock(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedStock, onStocks, showFilterSheet, setSelectedStock]);

  const optionLabel = useCallback((key, value) => {
    if (key === 'Kategori') return label(`categories.${value}`);
    if (key === 'ownershipType') return ownershipLabel(value);
    if (key === 'Durum') return statusLabel(value);
    return value;
  }, [label, ownershipLabel, statusLabel]);

  const columnLabel = useCallback((key) => label(`columns.${STOCK_COLUMNS[key].id}`), [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: {
      type: STOCK_COLUMNS[key].type,
      options: (STOCK_COLUMNS[key].options || []).map((value) => ({ value, label: optionLabel(key, value) })),
    },
  })), [columns.visibleKeys, columnLabel, optionLabel]);

  const filterChips = Object.keys(filters)
    .filter((key) => STOCK_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => {
      const { type } = STOCK_COLUMNS[key];
      let value = String(filters[key]);
      if (type === 'select') value = optionLabel(key, filters[key]);
      else if (type === 'date') {
        const [y, m, d] = value.split('-').map(Number);
        value = new Date(y, m - 1, d).toLocaleDateString(locale);
      }
      return { key, label: columnLabel(key), value, state: 'applied' };
    });

  const canClearFilters = filterChips.length > 0 || Boolean(searchTerm) || showExpiringOnly || showLowStockOnly;
  const isNarrowed = filteredStocks.length !== stocks.length;

  // ↑/↓ in the panel: step through the list, turning pages as needed
  const selectedIndex = selectedStock ? filteredStocks.findIndex((item) => item.id === selectedStock.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredStocks[selectedIndex + offset];
    if (!sibling) return;
    setSelectedStock(sibling);
    reveal(sibling.id);
  };

  const shareOf = (count) => (categoryStocks.length > 0 ? Math.min(1, count / categoryStocks.length) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const totalLabel = categoryFilter === 'all' ? label('stats.total') : label(`categories.${categoryFilter}`);
  const tiles = [
    { key: 'all', label: totalLabel, shortLabel: label('stats.totalShort'), value: categoryStocks.length, icon: LuPackage, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: activeStocks, icon: LuPackageCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(activeStocks), shareTitle: shareTitle(activeStocks), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500' },
    { key: 'inactive', label: label('stats.inactive'), shortLabel: label('stats.inactiveShort'), value: inactiveStocks, icon: LuPackageX, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', share: shareOf(inactiveStocks), shareTitle: shareTitle(inactiveStocks), barClass: 'tw-bg-gradient-to-r tw-from-slate-300 tw-to-slate-500' },
    { key: 'low', label: label('stats.low'), shortLabel: label('stats.lowShort'), value: lowStockItems, icon: LuTrendingDown, gradient: 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30', share: shareOf(lowStockItems), shareTitle: shareTitle(lowStockItems), barClass: 'tw-bg-gradient-to-r tw-from-rose-400 tw-to-pink-500' },
  ].map((tile) => ({ ...tile, onClick: () => setCardFilter(tile.key), active: cardFilter === tile.key }));

  const paneProps = {
    stock: onStocks ? selectedStock : null,
    low: selectedStock ? isLowStock(selectedStock) : false,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredStocks.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredStocks.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedStock(null),
    onEdit: stock.handleOpenEditModal,
    onAssign: stock.handleOpenMovementModal,
    onHistory: stock.handleOpenHistoryModal,
    onToggleCategory: stock.handleToggleCategory,
    onToggleStatus: stock.handleToggleStatus,
  };

  const typeOptions = stockTypes.map((type) => ({
    value: type.name,
    label: `${type.name} (${stocks.filter((s) => s['Stok Tipi'] === type.name).length})`,
  }));

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <StockHeader
          showActions={onStocks}
          onReport={() => stock.setShowReportModal(true)}
          onAssigned={stock.handleOpenAssignedStocksModal}
          onTypes={() => stock.setShowStockTypeModal(true)}
          onExportStocks={stock.downloadAllStocksExcel}
          onExportMovements={stock.handleExportAllStockMovements}
          onAdd={() => stock.setShowAddModal(true)}
        />

        {/* Modüller (Stoklar | Kargo Takip) ve kategori (Tümü | Sarf | Demirbaş) */}
        <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
          <div className="tw-max-w-full tw-overflow-x-auto">
            <SegmentedControl
              options={[
                { value: 'stocks', label: withCount(LuBoxes, label('modules.stocks')) },
                { value: 'cargo', label: withCount(LuTruck, label('modules.cargo')) },
              ]}
              value={activeModule}
              onChange={(value) => {
                setActiveModule(value);
                // Kargo hareketleri stok miktarlarını değiştirmiş olabilir
                if (value === 'stocks') fetchStocks();
              }}
              layoutId="stock-module"
              size={segmentSize}
              label={label('modules.label')}
            />
          </div>
          {onStocks && (
            <div className="tw-max-w-full tw-overflow-x-auto">
              <SegmentedControl
                options={[
                  { value: 'all', label: withCount(LuLayers, label('categories.all'), stocks.length, label('categories.allShort')) },
                  { value: 'sarf', label: withCount(LuBoxes, label('categories.sarf'), sarfCount, label('categories.sarfShort')) },
                  { value: 'demirbas', label: withCount(LuWrench, label('categories.demirbas'), demirbasCount) },
                ]}
                value={categoryFilter}
                onChange={setCategoryFilter}
                layoutId="stock-category"
                size={segmentSize}
                label={label('categories.label')}
              />
            </div>
          )}
        </div>
        {onStocks && categoryFilter !== 'all' && (
          <p className="-tw-mt-1 tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-leading-relaxed tw-text-slate-500 sm:-tw-mt-2 dark:tw-text-slate-400">
            <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-sky-500" />
            {label(`categories.${categoryFilter}Hint`)}
          </p>
        )}

        {/* Kargo Takip kendi sayfasında açılır; stok içeriği gizlenir (kaybolmaz) */}
        {activeModule === 'cargo' && <CargoTracking mode="company" />}

        <div className={onStocks ? 'tw-space-y-4 sm:tw-space-y-5' : 'tw-hidden'}>
          <StatTiles tiles={tiles} ready={stocksLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

          <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
            <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
              <DataToolbar
                search={{
                  value: searchTerm,
                  onChange: setSearchTerm,
                  placeholder: label('toolbar.search'),
                  placeholderShort: label('toolbar.searchShort'),
                  label: label('toolbar.search'),
                  minLength: 1,
                }}
                columnMenu={{
                  columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                  visibleColumns: columns.visibleColumns,
                  onToggle: columns.toggleColumn,
                  onShowAll: columns.showAllColumns,
                  onReset: columns.resetColumns,
                }}
                compact={compactList}
                onOpenFilters={() => setShowFilterSheet(true)}
                chips={filterChips}
                onRemoveChip={(key) => handleFilter(key, '')}
                onClearAll={clearFilters}
                extra={(
                  <>
                    {stockTypes.length > 0 && (
                      <span className="tw-flex tw-items-center tw-gap-1">
                        <LuTag aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                        <SelectInput
                          aria-label={label('toolbar.typeFilter')}
                          value={selectedTypeFilter || ''}
                          onChange={(e) => setSelectedTypeFilter(e.target.value || null)}
                          placeholder={label('toolbar.allTypes', { count: stocks.length })}
                          options={typeOptions}
                          className="tw-h-8 tw-min-w-[11rem] tw-rounded-full tw-text-xs"
                        />
                        {selectedTypeFilter && (
                          <button
                            type="button"
                            onClick={() => setSelectedTypeFilter(null)}
                            aria-label={label('toolbar.clearType')}
                            title={label('toolbar.clearType')}
                            className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
                          >
                            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
                          </button>
                        )}
                      </span>
                    )}
                    <ToggleChip
                      pressed={showExpiringOnly}
                      onChange={setShowExpiringOnly}
                      icon={LuCalendarClock}
                      label={label('toolbar.expiring')}
                      title={label('toolbar.expiringHint')}
                      tone="amber"
                    />
                    <ToggleChip
                      pressed={showLowStockOnly}
                      onChange={setShowLowStockOnly}
                      icon={LuTrendingDown}
                      label={label('toolbar.low')}
                      title={label('toolbar.lowHint')}
                      tone="violet"
                    />
                  </>
                )}
              />

              {compactList ? (
                <DataCardList
                  label={label('table.listLabel')}
                  rows={pageRows}
                  renderCard={(item) => <StockCardRow stock={item} low={isLowStock(item)} />}
                  selectedId={selectedStock?.id}
                  onSelect={handleStockSelect}
                  isLoading={!stocksLoaded}
                  emptyLabel={label('table.empty')}
                  canClearFilters={canClearFilters}
                  onClearFilters={clearFilters}
                />
              ) : (
                <DataTable
                  label={label('table.listLabel')}
                  rows={pageRows}
                  columns={tableColumns}
                  getColumnWidth={columns.getColumnWidth}
                  onResizeColumn={columns.setColumnWidth}
                  onResetColumnWidth={columns.resetColumnWidth}
                  pinnedKeys={PINNED_COLUMNS}
                  filters={filters}
                  onFilterChange={handleFilter}
                  getFilterState={getFilterState}
                  renderCell={(item, key) => <StockCell columnKey={key} stock={item} low={isLowStock(item)} />}
                  getCellTitle={getCellTitle}
                  getRowTone={(item) => (isLowStock(item) ? 'danger' : undefined)}
                  selectedId={selectedStock?.id}
                  onSelect={handleStockSelect}
                  isLoading={!stocksLoaded}
                  skeletonRows={pageSize}
                  maxCardHeight={maxCardHeight}
                  onCapacityChange={setPageSize}
                  emptyLabel={label('table.empty')}
                  canClearFilters={canClearFilters}
                  onClearFilters={clearFilters}
                  onShowColumns={columns.resetColumns}
                />
              )}

              <TablePagination
                rangeStart={pageStart + 1}
                rangeEnd={pageStart + pageRows.length}
                resultCount={filteredStocks.length}
                rangeOf={label('table.rangeOf', { total: filteredStocks.length.toLocaleString(locale) })}
                ofTotal={isNarrowed ? label('table.ofTotal', { total: stocks.length.toLocaleString(locale) }) : undefined}
                currentPage={currentPage}
                totalPages={totalPages}
                compact={compactList}
                onPage={goToPage}
              />
            </section>

            {dockPanel && <StockDetailPane docked height={fitHeight} {...paneProps} />}
          </div>
        </div>
      </div>

      {/* Below the docking width: a drawer over the page (portalled to <body>) */}
      {!dockPanel && <StockDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.map((key) => ({
          key,
          label: columnLabel(key),
          type: STOCK_COLUMNS[key].type,
          options: (STOCK_COLUMNS[key].options || []).map((value) => ({ value, label: optionLabel(key, value) })),
        }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredStocks.length}
      />

      <StockFormModal
        mode="add"
        isOpen={stock.showAddModal}
        stock={stock.newStock}
        onChange={stock.handleNewStockChange}
        onScopeChange={(field, value) => stock.setNewStock(stock.applyScopeChange(stock.newStock, field, value))}
        onSave={stock.handleAddStock}
        onClose={stock.handleCloseAddModal}
        stockTypes={stockTypes}
        customers={customers}
        projects={projects}
        projectsForCustomer={stock.projectsForCustomer}
        hasNoMatchingProject={stock.hasNoMatchingProject}
      />
      <StockFormModal
        mode="edit"
        isOpen={stock.showEditModal}
        stock={stock.editStock}
        onChange={stock.handleEditStockChange}
        onScopeChange={(field, value) => stock.setEditStock(stock.applyScopeChange(stock.editStock, field, value))}
        onSave={stock.handleUpdateStock}
        onClose={stock.handleCloseEditModal}
        stockTypes={stockTypes}
        customers={customers}
        projects={projects}
        projectsForCustomer={stock.projectsForCustomer}
        hasNoMatchingProject={stock.hasNoMatchingProject}
      />
      <StockAssignModal
        isOpen={stock.showMovementModal}
        stock={selectedStock}
        employees={activeEmployees}
        data={stock.movementData}
        setData={stock.setMovementData}
        onSave={stock.handleSaveMovement}
        onClose={stock.handleCloseMovementModal}
      />
      <StockHistoryModal
        isOpen={stock.showHistoryModal}
        stock={selectedStock}
        history={stock.stockHistory}
        onUndo={stock.handleUndoMovement}
        onExport={stock.handleExportStockHistory}
        onClose={stock.handleCloseHistoryModal}
      />
      <StockTypesModal
        isOpen={stock.showStockTypeModal}
        value={stock.newStockType}
        setValue={stock.setNewStockType}
        stockTypes={stockTypes}
        stocks={stocks}
        onAdd={stock.handleAddStockType}
        onDelete={stock.handleDeleteStockType}
        pendingDelete={stock.pendingTypeDelete}
        onConfirmDelete={stock.confirmDeleteStockType}
        onCancelDelete={() => stock.setPendingTypeDelete(null)}
        onClose={stock.handleCloseStockTypeModal}
      />
      <AssignedStocksModal
        isOpen={stock.showAssignedStocksModal}
        onClose={() => stock.setShowAssignedStocksModal(false)}
        loading={stock.assignedStocksLoading}
        data={stock.assignedStocksData}
        search={stock.assignedStocksSearch}
        setSearch={stock.setAssignedStocksSearch}
        viewMode={stock.assignedStocksViewMode}
        setViewMode={stock.setAssignedStocksViewMode}
        employeeFilter={stock.assignedEmployeeFilter}
        setEmployeeFilter={stock.setAssignedEmployeeFilter}
      />
      {/* Kullanım raporu: kime ne kadar verildi, ne kullanıldı, ne kaldı */}
      <StockReportModal show={stock.showReportModal} onClose={() => stock.setShowReportModal(false)} />
    </>
  );
};

export default StockManagementPage;
